import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const forced = authService.mustChangePassword();
  const user = authService.getCurrentUserFromStorage();

  const validateNewPassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Le nouveau mot de passe doit contenir au moins 8 caractères.';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Le nouveau mot de passe doit contenir au moins une majuscule.';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Le nouveau mot de passe doit contenir au moins un chiffre.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    const strengthError = validateNewPassword(newPassword);
    if (strengthError) {
      setError(strengthError);
      return;
    }

    if (newPassword === oldPassword) {
      setError('Le nouveau mot de passe doit être différent de l’ancien.');
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword(oldPassword, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du changement de mot de passe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow rounded-lg p-6 sm:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Changer le mot de passe</h1>
          <p className="mt-1 text-sm text-gray-600">
            {forced
              ? 'Vous devez définir un nouveau mot de passe pour continuer.'
              : 'Mettez à jour le mot de passe de votre compte.'}
          </p>
          {user?.email && (
            <p className="mt-2 text-xs text-gray-500">Compte : {user.email}</p>
          )}
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          {success && (
            <div className="rounded-md bg-green-50 border border-green-200 p-3">
              <p className="text-sm text-green-700">
                Mot de passe changé avec succès. Redirection…
              </p>
            </div>
          )}

          <div>
            <label htmlFor="old-password" className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe actuel
            </label>
            <input
              id="old-password"
              name="old-password"
              type="password"
              autoComplete="current-password"
              required
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 sm:text-sm"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={loading || success}
            />
          </div>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
              Nouveau mot de passe
            </label>
            <input
              id="new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              required
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 sm:text-sm"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading || success}
            />
            <p className="mt-1 text-xs text-gray-500">
              Minimum 8 caractères, une majuscule et un chiffre.
            </p>
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmer le nouveau mot de passe
            </label>
            <input
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 sm:text-sm"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading || success}
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            {!forced && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-sm text-gray-600 hover:text-gray-900"
                disabled={loading}
              >
                Annuler
              </button>
            )}
            <button
              type="submit"
              disabled={loading || success}
              className="ml-auto inline-flex justify-center rounded-md border border-transparent bg-emerald-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
